import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client'

import { useSites } from 'store'
import { TimeRange } from 'models'

import { convertDateRange, getScale } from '../../hooks/use-traffic'
import { useGetStatsTimeRange } from '../../store'
import { filterStatsByTimeRange, generateRanges } from '../use-utils'

const OPERATION_TRAFFIC = gql`
  query TrafficGraphGroupBySite($sfIds: [ID], $startsAt: String, $endsAt: String) {
    stats: trafficGraphGroupBySite(sf_ids: $sfIds, starts_at: $startsAt, ends_at: $endsAt) {
      site_id
      site_name
      requested_at
      download
      upload
      active_users
    }
  }
`

const generateSites = ({ stats, ranges }) => {
  const distinctSiteIds = stats.reduce((acc, { site_id }) => (acc.includes(site_id) ? acc : [...acc, site_id]), [])

  return ranges.map(range => {
    const statsInRange = filterStatsByTimeRange(stats, range.from, range.to)
    const values = distinctSiteIds.map(site_id => {
      const siteName = stats.find(stat => stat.site_id === site_id)?.site_name
      const totalActiveUsers = statsInRange.reduce((acc, stat) => {
        return stat.site_id === site_id ? acc + stat.active_users : acc
      }, 0)
      const totalDownload = statsInRange.reduce((acc, stat) => {
        return stat.site_id === site_id ? acc + stat.download : acc
      }, 0)
      const totalUpload = statsInRange.reduce((acc, stat) => {
        return stat.site_id === site_id ? acc + stat.upload : acc
      }, 0)
      return {
        site_id: site_id,
        name: siteName,
        active_users: totalActiveUsers,
        upload: totalUpload,
        download: totalDownload
      }
    })

    return {
      date: range.from,
      sites: values
    }
  })
}

const useGraph = ({modalTimeRange}) => {
  const [globalRange] = useGetStatsTimeRange()
  const [{ selected }] = useSites()
  const range = modalTimeRange || globalRange || TimeRange.LastTwelveMonths
  const scale = useMemo(() => {
    return getScale(range)
  }, [range])
  const dates = useMemo(() => {
    return convertDateRange(range)
  }, [range])

  const {loading, data} = useQuery(OPERATION_TRAFFIC, {
    variables: {
      sfIds: selected.map(({sf_id}) => sf_id),
      ...dates
    }
  })
  const formattedData = useMemo(() => {
    if (!data) {
      return []
    }
    return data.stats.map(stats => ({
      ...stats,
      download: parseInt(stats.download) / 1000,
      upload: parseInt(stats.upload) / 1000,
      active_users: parseInt(stats.active_users),
    }))
  }, [data])

  const graph = useMemo(() => {
    let ranges = generateRanges({...dates, scale})
    // le dernier jour / mois est supprimé
    if (getScale(range) !== 'year') {
      ranges = ranges.slice(0,-1)
    }
    const graph = generateSites({stats: formattedData, ranges})
    return graph.map(stats => {
      const download = stats.sites.reduce((acc, site) => {
        return acc + site.upload + site.download
      }, 0)
      const activeUsers = stats.sites.reduce((acc, site) => {
        return acc + site.active_users
      }, 0)
      return {
        ...stats,
        average_per_device_site: activeUsers ? (download / activeUsers) : 0

      }
    })
  }, [formattedData, range])

  return {
    loading,
    graph
  }
}

export default useGraph


